import { useTu } from "@src/appV2/i18n/useTu";

/* eslint-disable import/max-dependencies, max-lines -- Login page orchestrates multiple auth flows (magic link, password, forgot password, confirmations) and composes desktop/mobile layouts */
import { Box, Button, CircularProgress, Link } from "@mui/material";

import { ThemeProvider } from "@mui/material/styles";
import { ensureCognitoUser } from "@src/appV2/Auth/cognito/cognitoAuth";
import { verifyEmailOtp } from "@src/appV2/Auth/cognito/emailOtpAuth";
import { requestPasswordReset } from "@src/appV2/Auth/cognito/cognitoPasswordAuth";
import { clearMagicLinkEmail } from "@src/appV2/Auth/cognito/magicLinkEmailStore";
import {
  initCognitoSession,
  SESSION_INIT_OUTCOME,
} from "@src/appV2/Auth/cognito/initCognitoSession";
import { isSafeRedirectUrl } from "@src/appV2/Auth/cognito/isSafeRedirectUrl";
import {
  clearRedirectInProgress,
  hasCognitoTokens,
  isRedirectInProgress,
  markRedirectInProgress,
} from "@src/appV2/Auth/cognito/redirectInProgress";
import { mapCognitoError } from "@src/appV2/Auth/cognito/mapCognitoError";
import { isPasskeySupported, signInWithPasskey } from "@src/appV2/Auth/cognito/cognitoWebAuthnAuth";
import { getRememberedEmail } from "@src/appV2/Auth/rememberEmail/rememberEmailStore";
import { requestRememberEmail } from "@src/appV2/Auth/rememberEmail/requestRememberEmail";
import {
  classifyCognitoError,
  IDENTITY_PROVIDER,
  isNetworkFailure,
  LOGIN_OUTCOME,
  LOGIN_REASON,
  logPasswordLoginFailure,
  logPasswordResetRequestFailure,
  setLoginIdentityProvider,
} from "@src/appV2/Auth/loginRum";
import { MOBILE_SSO_CODE_VERIFIER_KEY } from "@src/appV2/Auth/RipplingSso/startSsoRedirect";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { APP_V2_APP_EVENTS, APP_V3_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { OutageBanner } from "@src/appV2/OutageBanner/OutageBanner";
import { useOutageBanner } from "@src/appV2/OutageBanner/useOutageBanner";
import { useIsDesktop } from "@src/appV2/redesign/AppLayout/useResponsive";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { Text } from "@src/appV2/redesign/components/Text";
import { TextInput } from "@src/appV2/redesign/components/TextInput";
import { berlinRebrandTheme } from "@src/appV2/redesign/theme/theme";
import { loginWithEmailAndPassword } from "@src/mobile/Login/loginWithEmailAndPassword";
import { sendEmailOtp } from "@src/mobile/Login/sendEmailOtp";
import { sendLoginEmail } from "@src/mobile/Login/sendLoginEmail";
import { type ReactElement, useCallback, useEffect, useState } from "react";

// eslint-disable-next-line no-restricted-imports -- Dispatching Cognito session thunk after password login
import { useDispatch } from "react-redux";

import { useHistory, useLocation } from "react-router-dom";
import loginIllustration from "./assets/login-illustration.webp";
import { DesktopLoginLayout } from "./DesktopLoginLayout";
import { LoginForm } from "./LoginForm";
import { LoginWithEmailAndPasswordForm } from "./LoginWithEmailAndPasswordForm";
import { MobileLoginLayout } from "./MobileLoginLayout";
import { OtpEntryForm } from "./OtpEntryForm";
import { useResendCooldown } from "./useResendCooldown";
import { useSsoLogin } from "./useSsoLogin";
const WORKPLACE_SIGNUP_URL = "https://www.clipboardworks.com/workplaces/signup";
type LoginMode = "magicLink" | "password" | "forgotPassword" | "resetEmailSent";

const VALID_LOGIN_MODES: readonly LoginMode[] = [
  "magicLink",
  "password",
  "forgotPassword",
  "resetEmailSent",
];

function parseInitialLoginMode(search: string): LoginMode {
  const mode = new URLSearchParams(search).get("mode");
  return VALID_LOGIN_MODES.find((m) => m === mode) ?? "magicLink";
}

/**
 * Clears stale OAuth/PKCE state from previous failed SSO attempts so the
 * login page starts clean. Only clears OAuth flow state — not Cognito
 * tokens or session data, which are managed by the logout flow.
 */
function clearStaleAuthState(): void {
  const keysToRemove: string[] = [];
  for (let index = 0; index < localStorage.length; index++) {
    const key = localStorage.key(index);
    if (key?.includes("oauthState") || key?.includes("oauthPKCE")) {
      keysToRemove.push(key);
    }
  }
  for (const key of keysToRemove) {
    localStorage.removeItem(key);
  }
  localStorage.removeItem(MOBILE_SSO_CODE_VERIFIER_KEY);
}

export function Login(): ReactElement {
  const { tu } = useTu("login");

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch = useDispatch<any>();

  const history = useHistory();
  const location = useLocation();
  // Pre-fill the email remembered on this device, if the user opted in before.
  const [email, setEmail] = useState(() => getRememberedEmail() ?? "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  // Whether the passwordless flow has advanced to its second step: the OTP
  // code-entry form when the email-OTP flag is on, or the "magic link sent"
  // confirmation when it's off.
  const [passwordlessRequested, setPasswordlessRequested] = useState(false);
  // Set once any login flow succeeds and we start navigating to the dashboard,
  // so we swap to a full-page "signing in" state instead of any login form.
  // Initialized from the cross-mount signal so a remount triggered by the
  // post-login session-hydration bounce shows the spinner, not the form.
  const [isRedirecting, setIsRedirecting] = useState(isRedirectInProgress);

  const [loginMode, setLoginMode] = useState<LoginMode>(() =>
    parseInitialLoginMode(location.search)
  );

  const [loginError, setLoginError] = useState<string | undefined>();
  const [resetError, setResetError] = useState<string | undefined>();
  const [passwordlessError, setPasswordlessError] = useState<string | undefined>();
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [passkeyError, setPasskeyError] = useState<string | undefined>();

  const isPasskeyAuthEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_ADMIN_APP_PASSKEY_AUTH, {
    defaultValue: false,
  });
  // When on, the passwordless flow sends a 6-digit email OTP and prompts for the
  // code; when off (default), it falls back to the original magic-link email.
  const isEmailOtpEnabled = useCbhFlag(CbhFeatureFlag.EMAIL_OTP_INSTEAD_OF_MAGIC_LINK, {
    defaultValue: false,
  });
  const passkeySupported = isPasskeySupported();

  // Clear stale auth state when the login page mounts. Previous failed
  // SSO attempts or stale Cognito tokens can leave state that causes
  // infinite loading loops or redirect errors on retry.
  useEffect(() => {
    clearStaleAuthState();
    // Drop a leftover redirect signal when there are no Cognito tokens — that
    // means we're not mid-login (e.g. landed here after logout), so show the
    // form rather than a perpetual spinner.
    if (!hasCognitoTokens()) {
      clearRedirectInProgress();
      setIsRedirecting(false);
    }
  }, []);

  const { ssoMode, handleSsoRedirect } = useSsoLogin(email);
  const { secondsRemaining, isCoolingDown, startCooldown } = useResendCooldown();

  // Enters the "signing in" state and persists the cross-mount signal. MUST be
  // called BEFORE `dispatch(initCognitoSession())`: that dispatch flips
  // `isAuthorized` mid-await, which remounts <Login>; the signal has to already
  // be set so the fresh instance shows the spinner instead of flashing the form.
  const beginRedirect = useCallback(() => {
    markRedirectInProgress();
    setIsRedirecting(true);
  }, []);

  // Reverts the "signing in" state when a login that started redirecting fails
  // before it navigates (e.g. session hydration fails), so the form returns.
  const cancelRedirect = useCallback(() => {
    clearRedirectInProgress();
    setIsRedirecting(false);
  }, []);

  // Navigates to the post-login destination. The "signing in" state is already
  // in effect (see `beginRedirect`), so no login form renders during the
  // redirect. Shared by every success path (email OTP, password, passkey).
  const redirectAfterLogin = useCallback(() => {
    const storedRedirectUrl = localStorage.getItem("redirectURL");
    const redirectUrl =
      storedRedirectUrl && isSafeRedirectUrl(storedRedirectUrl) ? storedRedirectUrl : "/";
    localStorage.removeItem("redirectURL");
    history.replace(redirectUrl);
    // Clear the shared signal now that the hand-off has happened — the bounce
    // remount already occurred during session hydration (before this runs), and
    // we're now navigating away from /login. Leaving it set would let a later
    // same-session AuthRedirect visit short-circuit straight to the dashboard
    // (its shortcut treats the flag + live Cognito tokens as "redirect in flight").
    clearRedirectInProgress();
  }, [history]);

  // Requests an email OTP for the current email and starts the resend cooldown
  // on success. Shared by the initial submit and the code-entry resend; returns
  // whether the request succeeded so callers can switch to the code-entry view.
  const sendOtp = useCallback(
    async (options?: { isResend?: boolean }) => {
      setLoading(true);
      setPasswordlessError(undefined);

      const result = await sendEmailOtp({
        email,
        // The initial send already JIT-imported the user; a resend shouldn't
        // re-trigger the PreSignUp Lambda (DEVOP-5526).
        skipEnsureUser: options?.isResend ?? false,
      });

      if (result.success) {
        startCooldown();
      } else {
        setPasswordlessError(result.error);
      }

      setLoading(false);
      return result.success;
    },
    [email, startCooldown]
  );

  // Sends the magic link to the current email and starts the resend cooldown on
  // success. Shared by the initial submit and the confirmation-screen resend;
  // returns whether the send succeeded so callers can react (e.g. show the
  // confirmation screen).
  const sendMagicLink = useCallback(
    async (options?: { isResend?: boolean }) => {
      setLoading(true);
      setPasswordlessError(undefined);

      const result = await sendLoginEmail({
        email,
        requestedURL: localStorage.getItem("redirectURL") ?? undefined,
        // The initial send already JIT-imported the user; a resend shouldn't
        // re-trigger the PreSignUp Lambda (DEVOP-5526).
        skipEnsureUser: options?.isResend ?? false,
      });

      if (result.success) {
        startCooldown();
      } else {
        setPasswordlessError(result.error);
      }

      setLoading(false);
      return result.success;
    },
    [email, startCooldown]
  );

  const handlePasswordlessSubmit = useCallback(async () => {
    if (!email) {
      return;
    }

    setPasskeyError(undefined);

    // Production: SSO replaces the email flow for CBH employees
    if (ssoMode === "sso_only") {
      setLoading(true);
      setPasswordlessError(undefined);
      await handleSsoRedirect("submit");
      setLoading(false);
      return;
    }

    const requested = isEmailOtpEnabled ? await sendOtp() : await sendMagicLink();
    if (requested) {
      setPasswordlessRequested(true);
    }
  }, [email, ssoMode, handleSsoRedirect, isEmailOtpEnabled, sendOtp, sendMagicLink]);

  // Resends the magic link to the same email from the confirmation screen.
  // Gated by a cooldown so users can't spam the endpoint; the cooldown
  // restarts on every successful resend. Magic-link flow only.
  const handleResendEmail = useCallback(async () => {
    if (!email || isCoolingDown) {
      return;
    }

    await sendMagicLink({ isResend: true });
  }, [email, isCoolingDown, sendMagicLink]);

  // Resends the email OTP to the same address from the code-entry screen. Gated
  // by the same cooldown as the magic link resend so users can't spam the
  // endpoint; the cooldown restarts on every successful resend. Email-OTP flow
  // only.
  const handleResendOtp = useCallback(async () => {
    if (!email || isCoolingDown) {
      return;
    }

    await sendOtp({ isResend: true });
  }, [email, isCoolingDown, sendOtp]);

  // Verifies the code typed on this (the requesting) browser. A single wrong
  // code fails the Cognito flow — the user can re-enter the code, resend a new
  // one, or go back.
  const handleVerifyOtp = useCallback(
    async (code: string) => {
      setLoading(true);
      setPasswordlessError(undefined);

      try {
        await verifyEmailOtp(code);

        // Enter the redirect state BEFORE hydrating the session — that dispatch
        // remounts <Login>, and the signal must already be set so the remount
        // shows the spinner rather than flashing the login form.
        beginRedirect();
        const outcome = await dispatch(initCognitoSession());
        if (outcome === SESSION_INIT_OUTCOME.WORKER_ACCOUNT) {
          // Worker signed in at the workplace/admin login — friendly redirect
          // copy, not the misleading "session not restored" retry message.
          cancelRedirect();
          setPasswordlessError(
            tu(
              'The submitted code was correct, but this email belongs to a healthcare professional account, which is not allowed to post shifts and therefore cannot access this app. Please use the mobile app "Clipboard - Workers" to sign in instead.'
            )
          );
          setLoading(false);
          return;
        }
        if (outcome !== SESSION_INIT_OUTCOME.HYDRATED) {
          // The code verified but session hydration failed — a genuine defect,
          // not user friction. Record it as an unexpected error so it surfaces
          // in Error Tracking, mirroring the password flow's session-init path.
          cancelRedirect();
          logPasswordLoginFailure({
            eventName: APP_V3_APP_EVENTS.EMAIL_OTP_LOGIN_FAILURE,
            error: new Error("Cognito session not restored after email OTP verification"),
            metadata: {
              outcome: LOGIN_OUTCOME.UNEXPECTED_ERROR,
              reason: LOGIN_REASON.SESSION_HYDRATION_FAILED,
            },
          });
          setPasswordlessError(
            tu(
              "We verified your code, but couldn't load your account. Please try again in a moment, or contact support if the problem continues."
            )
          );
          setLoading(false);
          return;
        }

        logEvent(APP_V3_APP_EVENTS.EMAIL_OTP_LOGIN_SUCCESS, { outcome: LOGIN_OUTCOME.SUCCESS });

        // Request the "remember your email?" prompt only now that hydration
        // succeeded. Requested earlier, it opens a focus-trapping dialog over
        // the auth page; if login then fails (worker account, hydration error),
        // the dialog stays stuck open and freezes the form, and its pending flag
        // re-opens it on reload.
        requestRememberEmail(email);

        clearMagicLinkEmail();
        redirectAfterLogin();
      } catch (error: unknown) {
        cancelRedirect();
        const mapped = mapCognitoError(error);
        // Classify so expected friction (wrong/expired code) is recorded as a
        // Datadog action while genuine defects keep the error attached and
        // reach Error Tracking, matching the password-login pattern.
        const { outcome, reason } =
          mapped.code === "auth/unknown" && isNetworkFailure(error)
            ? { outcome: LOGIN_OUTCOME.EXPECTED_ERROR, reason: LOGIN_REASON.NETWORK_FAILURE }
            : classifyCognitoError(mapped.code);
        logPasswordLoginFailure({
          eventName: APP_V3_APP_EVENTS.EMAIL_OTP_LOGIN_FAILURE,
          error,
          metadata: { errorCode: mapped.code, errorMessage: mapped.message, outcome, reason },
        });
        // A wrong/expired native EMAIL_OTP surfaces as CodeMismatchException
        // (auth/invalid-code) or ExpiredCodeException (auth/expired-code); an
        // expired sign-in session (NotAuthorizedException "session is expired")
        // also maps to auth/expired-code. Network/unknown failures map to
        // auth/unknown. Show the OTP-specific retry copy for all of them so users
        // don't get magic-link/reset guidance.
        const isOtpRetryable =
          mapped.code === "auth/unknown" ||
          mapped.code === "auth/invalid-code" ||
          mapped.code === "auth/expired-code";
        setPasswordlessError(
          isOtpRetryable
            ? tu("That code is incorrect or expired. Go back and request a new code.")
            : mapped.message
        );
        setLoading(false);
      }
    },
    [email, dispatch, beginRedirect, cancelRedirect, redirectAfterLogin, tu]
  );

  const handlePasswordSubmit = useCallback(async () => {
    if (!email || !password) {
      return;
    }

    setLoading(true);
    setLoginError(undefined);

    const result = await loginWithEmailAndPassword({ email, password });

    if (result.success) {
      // Enter the redirect state BEFORE hydrating the session — that dispatch
      // remounts <Login>, and the signal must already be set so the remount
      // shows the spinner rather than flashing the login form.
      beginRedirect();
      // Hydrate Redux session so PrivateRoute sees a valid session immediately,
      // matching what AuthRedirect does for the magic link flow.
      const outcome = await dispatch(initCognitoSession());
      if (outcome === SESSION_INIT_OUTCOME.WORKER_ACCOUNT) {
        // Wrong-app worker sign-in (see handleVerifyOtp). Friendly redirect copy.
        cancelRedirect();
        setLoginError(
          tu(
            'Your password was correct, but this email belongs to a healthcare professional account, which is not allowed to post shifts and therefore cannot access this app. Please use the mobile app "Clipboard - Workers" to sign in instead.'
          )
        );
        setLoading(false);
        return;
      }
      if (outcome !== SESSION_INIT_OUTCOME.HYDRATED) {
        cancelRedirect();
        setLoginError(
          tu(
            "We signed you in, but couldn't load your account. Please try again in a moment, or contact support if the problem continues."
          )
        );
        setLoading(false);
        return;
      }

      // Request the "remember your email?" prompt only after hydration
      // succeeds (see handleVerifyOtp) so a failed login never leaves the
      // focus-trapping dialog stuck over the form.
      requestRememberEmail(email);

      redirectAfterLogin();
      return;
    }

    setLoginError(result.error);
    setLoading(false);
  }, [email, password, dispatch, beginRedirect, cancelRedirect, redirectAfterLogin, tu]);

  // Reaches `signInWithPasskey` synchronously from the click (no prior await)
  // so the WebAuthn ceremony stays inside the user gesture.
  const handlePasskeyClick = useCallback(async () => {
    if (!email) {
      return;
    }

    setPasskeyLoading(true);
    setPasskeyError(undefined);
    setPasswordlessError(undefined);
    setLoginIdentityProvider(IDENTITY_PROVIDER.COGNITO_PASSKEY);
    logEvent(APP_V2_APP_EVENTS.PASSKEY_LOGIN_STARTED, { hasEmail: Boolean(email) });

    // Cognito's user-existence-error prevention fabricates a challenge for
    // unknown emails (sometimes factor selection → "no-passkey", sometimes a
    // real WEB_AUTHN ceremony, sometimes a NotAuthorized/UserNotFound error), so
    // the client genuinely can't tell "no account", "account without a passkey",
    // and "wrong passkey" apart. Show one honest message for every passkey
    // failure instead of leaking a misleading user-existence claim.
    const passkeyUnavailableMessage = tu(
      "We couldn't sign you in with a passkey for this email. If you have an account, sign in with a link or password, then add a passkey from the My Account page."
    );

    try {
      const result = await signInWithPasskey(email.trim());

      if (result.status === "no-passkey") {
        logEvent(APP_V2_APP_EVENTS.PASSKEY_LOGIN_NO_CREDENTIAL);
        setPasskeyError(passkeyUnavailableMessage);
        setPasskeyLoading(false);
        return;
      }

      // Enter the redirect state BEFORE hydrating the session — that dispatch
      // remounts <Login>, and the signal must already be set so the remount
      // shows the spinner rather than flashing the login form.
      beginRedirect();
      const outcome = await dispatch(initCognitoSession({ authMethod: "passkey" }));
      if (outcome === SESSION_INIT_OUTCOME.WORKER_ACCOUNT) {
        // Wrong-app worker sign-in (see handleVerifyOtp). Friendly redirect copy.
        cancelRedirect();
        setPasskeyError(
          tu(
            'Your passkey was verified, but this email belongs to a healthcare professional account, which is not allowed to post shifts and therefore cannot access this app. Please use the mobile app "Clipboard - Workers" to sign in instead.'
          )
        );
        setPasskeyLoading(false);
        return;
      }
      if (outcome !== SESSION_INIT_OUTCOME.HYDRATED) {
        cancelRedirect();
        setPasskeyError(
          tu(
            "We verified your passkey, but couldn't load your account. Please try again in a moment, or contact support if the problem continues."
          )
        );
        setPasskeyLoading(false);
        return;
      }

      logEvent(APP_V2_APP_EVENTS.PASSKEY_LOGIN_SUCCESS, { outcome: LOGIN_OUTCOME.SUCCESS });
      // Request the "remember your email?" prompt only after hydration
      // succeeds (see handleVerifyOtp) so a failed login never leaves the
      // focus-trapping dialog stuck over the form.
      requestRememberEmail(email);
      redirectAfterLogin();
    } catch (error: unknown) {
      cancelRedirect();
      const mapped = mapCognitoError(error, "passkey");
      logError(APP_V2_APP_EVENTS.PASSKEY_LOGIN_FAILURE, {
        error,
        metadata: { errorCode: mapped.code },
      });
      // A user-not-found error here is just prevention noise — the email may
      // well be valid. Collapse it into the neutral message; keep genuine
      // passkey errors (cancelled, security, unsupported) actionable.
      setPasskeyError(
        mapped.code === "auth/user-not-found" ? passkeyUnavailableMessage : mapped.message
      );
      setPasskeyLoading(false);
    }
  }, [email, dispatch, beginRedirect, cancelRedirect, redirectAfterLogin, tu]);

  const handleForgotPassword = useCallback(async () => {
    if (!email) {
      setResetError(tu("Please enter your email address first."));
      return;
    }

    setLoading(true);
    setResetError(undefined);

    try {
      await ensureCognitoUser(email);
      await requestPasswordReset(email);
      logEvent(APP_V2_APP_EVENTS.COGNITO_PASSWORD_RESET_REQUESTED);
      setLoginMode("resetEmailSent");
    } catch (error: unknown) {
      const mapped = mapCognitoError(error, "password");
      logPasswordResetRequestFailure({ error, metadata: { errorCode: mapped.code } });
      setResetError(mapped.message);
    } finally {
      setLoading(false);
    }
  }, [email, tu]);

  // Clear any stale passkey error when the email changes, so a "no passkey for
  // this email" message doesn't linger against a different address.
  const handleEmailChange = useCallback((value: string) => {
    setEmail(value);
    setPasskeyError(undefined);
  }, []);

  const isDesktop = useIsDesktop();
  const outageBanner = useOutageBanner();
  const banner = outageBanner.isVisible ? <OutageBanner /> : undefined;

  // While redirecting, hide every login affordance (illustration, heading, and
  // all forms) so only the "signing in" indicator shows during the hand-off.
  const showIllustration =
    !isRedirecting &&
    !(loginMode === "magicLink" && passwordlessRequested) &&
    loginMode !== "resetEmailSent";

  const showWelcomeHeading =
    !isRedirecting &&
    ((loginMode === "magicLink" && !passwordlessRequested) || loginMode === "password");

  const signingInBox = (
    <Box
      sx={{
        width: "100%",
        maxWidth: 400,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 4,
        paddingY: 8,
      }}
    >
      <CircularProgress />
      <Text variant="body1" sx={{ color: "base.muted" }}>
        {tu("Signing you in…")}
      </Text>
    </Box>
  );

  const formBox = (
    <Box sx={{ width: "100%", maxWidth: 400 }}>
      {loginMode === "magicLink" && !passwordlessRequested && (
        <LoginForm
          email={email}
          isSsoEmail={ssoMode === "sso_only"}
          loading={loading}
          showSsoButton={ssoMode === "sso_and_magic_link"}
          errorMessage={passwordlessError}
          showPasskeyButton={isPasskeyAuthEnabled && passkeySupported && ssoMode !== "sso_only"}
          passkeyLoading={passkeyLoading}
          passkeyError={passkeyError}
          setEmail={handleEmailChange}
          onSsoClick={() => handleSsoRedirect("sso_button")}
          onSubmit={handlePasswordlessSubmit}
          onPasskeyClick={() => {
            void handlePasskeyClick();
          }}
          onPasskeyInvalidEmail={() => {
            setPasskeyError(tu("Please enter a valid email address to sign in with a passkey."));
          }}
          onSwitchToPassword={() => {
            setPasswordlessError(undefined);
            setPasskeyError(undefined);
            setLoginMode("password");
          }}
        />
      )}

      {loginMode === "magicLink" && passwordlessRequested && isEmailOtpEnabled && (
        <OtpEntryForm
          email={email}
          loading={loading}
          errorMessage={passwordlessError}
          isResendCoolingDown={isCoolingDown}
          resendSecondsRemaining={secondsRemaining}
          onSubmit={handleVerifyOtp}
          onResend={() => {
            void handleResendOtp();
          }}
          onBack={() => {
            setPasswordlessError(undefined);
            setPasswordlessRequested(false);
          }}
        />
      )}

      {loginMode === "magicLink" && passwordlessRequested && !isEmailOtpEnabled && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Text sx={{ color: "primary.main", fontSize: "1.25rem" }}>✓</Text>
            <Text bold variant="h5" sx={{ color: "base.base" }}>
              {tu("Verification link sent")}
            </Text>
          </Box>
          <Text bold variant="body1" sx={{ color: "base.muted", textAlign: "center" }}>
            {tu("We have sent an email with a login link to {{email}}.", {
              values: { email },
            })}
          </Text>
          {passwordlessError && (
            <Text variant="body2" role="alert" sx={{ color: "error.main", textAlign: "center" }}>
              {passwordlessError}
            </Text>
          )}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
              width: "100%",
            }}
          >
            <LoadingButton
              fullWidth
              variant="primary"
              size="large"
              isLoading={loading}
              disabled={isCoolingDown}
              onClick={() => {
                void handleResendEmail();
              }}
            >
              {isCoolingDown
                ? tu("Resend email in {{seconds}}s", { values: { seconds: secondsRemaining } })
                : tu("Resend email")}
            </LoadingButton>
            <Button
              variant="text"
              disabled={loading}
              sx={{ textTransform: "none", color: "primary.main" }}
              onClick={() => {
                setPasswordlessError(undefined);
                setPasswordlessRequested(false);
              }}
            >
              {tu("Go back to login page")}
            </Button>
          </Box>
        </Box>
      )}

      {loginMode === "password" && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            width: "100%",
          }}
        >
          <LoginWithEmailAndPasswordForm
            email={email}
            password={password}
            loading={loading}
            errorMessage={loginError}
            setEmail={setEmail}
            setPassword={setPassword}
            onSubmit={handlePasswordSubmit}
            onForgotPassword={() => {
              setLoginError(undefined);
              setLoginMode("forgotPassword");
            }}
          />
          {/* Divider */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              alignSelf: "stretch",
              gap: 5,
              marginTop: 4,
            }}
          >
            <Box
              sx={{
                flex: 1,
                height: "1px",
                backgroundColor: "border.base",
              }}
            />
            <Text variant="body2" sx={{ color: "base.inactive" }}>
              {tu("or")}
            </Text>
            <Box
              sx={{
                flex: 1,
                height: "1px",
                backgroundColor: "border.base",
              }}
            />
          </Box>

          <Button
            variant="text"
            disabled={loading}
            sx={{
              textTransform: "none",
              color: "primary.main",
              marginTop: 4,
              alignSelf: "center",
            }}
            onClick={() => {
              setLoginMode("magicLink");
            }}
          >
            {tu("Sign in with link")}
          </Button>
        </Box>
      )}

      {loginMode === "forgotPassword" && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            gap: 4,
            width: "100%",
          }}
        >
          <Text bold variant="body1" sx={{ textAlign: "center" }}>
            {tu("Set/reset password")}
          </Text>
          <Text variant="body2" sx={{ color: "base.muted", textAlign: "center" }}>
            {tu("Enter your email address and we'll send you a link to set/reset your password.")}
          </Text>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Text semibold variant="body2" sx={{ color: "base.muted" }}>
              {tu("Email")}
            </Text>
            <TextInput
              fullWidth
              required
              type="email"
              placeholder="your.email@example.com"
              value={email}
              disabled={loading}
              autoComplete="email"
              inputProps={{ "aria-label": "Email" }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "1rem",
                  backgroundColor: "background.standout",
                },
              }}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
            />
          </Box>
          {resetError && (
            <Text variant="body2" role="alert" sx={{ color: "error.main", textAlign: "center" }}>
              {resetError}
            </Text>
          )}
          <LoadingButton
            fullWidth
            variant="primary"
            size="large"
            isLoading={loading}
            disabled={!email}
            onClick={() => {
              void handleForgotPassword();
            }}
          >
            {tu("Send Set/Reset Link")}
          </LoadingButton>

          <Button
            variant="text"
            disabled={loading}
            sx={{
              textTransform: "none",
              color: "primary.main",
              alignSelf: "center",
            }}
            onClick={() => {
              setResetError(undefined);
              setLoginMode("password");
            }}
          >
            {tu("Back to sign in")}
          </Button>
        </Box>
      )}

      {loginMode === "resetEmailSent" && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Text sx={{ color: "primary.main", fontSize: "1.25rem" }}>✓</Text>
            <Text bold variant="h5" sx={{ color: "base.base" }}>
              {tu("Reset link sent")}
            </Text>
          </Box>
          <Text bold variant="body1" sx={{ color: "base.muted", textAlign: "center" }}>
            {tu(
              "We have sent a password reset link to {{email}}. Check your email and follow the instructions.",
              { values: { email } }
            )}
          </Text>
          <Button
            variant="text"
            sx={{ textTransform: "none", color: "primary.main" }}
            onClick={() => {
              setLoginMode("password");
            }}
          >
            {tu("Back to sign in")}
          </Button>
        </Box>
      )}

      {showIllustration && (
        <Box
          sx={{
            marginTop: 6,
            display: "flex",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <Text variant="body2" sx={{ color: "base.muted" }}>
            {tu("Don't have a workplace account?")}
          </Text>
          <Link
            href={WORKPLACE_SIGNUP_URL}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{ fontSize: "0.875rem", color: "primary.main" }}
            onClick={() => {
              logEvent(APP_V2_APP_EVENTS.LOGIN_SIGNUP_LINK_CLICKED);
            }}
          >
            {tu("Sign up")}
          </Link>
        </Box>
      )}
    </Box>
  );

  if (isDesktop) {
    return (
      <ThemeProvider theme={berlinRebrandTheme}>
        <DesktopLoginLayout banner={banner}>
          {showIllustration && (
            <img
              src={loginIllustration}
              alt="Person sitting at a desk"
              style={{ height: "min(212px, 22vh)", width: "auto" }}
            />
          )}
          {showWelcomeHeading && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                width: "100%",
              }}
            >
              <Text
                variant="h5"
                sx={{
                  fontSize: "2rem",
                  lineHeight: 1.2,
                  color: "base.base",
                  textAlign: "center",
                }}
              >
                {tu("Welcome back")}
              </Text>
            </Box>
          )}
          {isRedirecting ? signingInBox : formBox}
        </DesktopLoginLayout>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={berlinRebrandTheme}>
      <MobileLoginLayout banner={banner}>
        {showIllustration && (
          <img
            src={loginIllustration}
            alt="Person sitting at a desk"
            style={{ height: 170, width: "auto", marginBottom: 40 }}
          />
        )}
        {isRedirecting ? signingInBox : formBox}
      </MobileLoginLayout>
    </ThemeProvider>
  );
}
/* eslint-enable import/max-dependencies, max-lines */
